import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    InputAdornment,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import TagSelector from './TagSelector';
import LoadingButton from './LoadingButton';
import { currencySymbol } from '../utils/money';
import { categoryMenuItems } from '../utils/categoryMenu';

const AddTransactionDialog = ({ open, onClose, onAdd, categories, tags }) => {
    const [formData, setFormData] = useState({
        date: new Date(),
        time: null,
        description: '',
        amount: '',
        type: 'expense',
        category: '',
        accountType: 'bank_account',
        bank: '',
        accountId: '',
        user: '',
        tagIds: []
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // Reset form when dialog opens
    useEffect(() => {
        if (open) {
            setFormData({
                date: new Date(),
                time: null,
                description: '',
                amount: '',
                type: 'expense',
                category: '',
                accountType: 'bank_account',
                bank: '',
                accountId: '',
                user: '',
                tagIds: []
            });
            setErrors({});
        }
    }, [open]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error for this field when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.description.trim()) {
            newErrors.description = 'Description is required';
        }

        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            newErrors.amount = 'Valid amount is required';
        }

        if (!formData.date) {
            newErrors.date = 'Date is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) {
            return;
        }

        setLoading(true);
        try {
            // Format date for API
            const dateStr = formData.date.toLocaleDateString('en-GB'); // DD/MM/YYYY format

            // Format time if available (HH:MM)
            let timeStr = null;
            if (formData.time) {
                const hours = formData.time.getHours().toString().padStart(2, '0');
                const minutes = formData.time.getMinutes().toString().padStart(2, '0');
                timeStr = `${hours}:${minutes}`;
            }

            const transactionData = {
                date: dateStr,
                time: timeStr,
                description: formData.description.trim(),
                amount: parseFloat(formData.amount),
                type: formData.type,
                category: formData.category || null,
                accountType: formData.accountType,
                bank: formData.bank.trim() || null,
                accountId: formData.accountId.trim() || null,
                user: formData.user.trim() || null,
                tagIds: formData.tagIds
            };

            await onAdd(transactionData);
            onClose();
        } catch (error) {
            console.error('Error submitting transaction:', error);
            // Error handling is done in parent component
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
                sx: { minHeight: '600px' }
            }}
        >
            <DialogTitle>Add Manual Transaction</DialogTitle>
            <DialogContent>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Grid container spacing={3} sx={{ mt: 0.5 }}>
                        {/* Row 1: Date and Time */}
                        <Grid item xs={12} md={6}>
                            <DatePicker
                                label="Date *"
                                value={formData.date}
                                onChange={(newDate) => handleChange('date', newDate)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: !!errors.date,
                                        helperText: errors.date
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TimePicker
                                label="Time (Optional)"
                                value={formData.time}
                                onChange={(newTime) => handleChange('time', newTime)}
                                slotProps={{
                                    textField: {
                                        fullWidth: true
                                    }
                                }}
                            />
                        </Grid>

                        {/* Row 2: Description - Full Width */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description *"
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                error={!!errors.description}
                                helperText={errors.description}
                                placeholder="e.g., Grocery shopping at ABC Store"
                            />
                        </Grid>

                        {/* Row 3: Amount and Type */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Amount *"
                                value={formData.amount}
                                onChange={(e) => handleChange('amount', e.target.value)}
                                error={!!errors.amount}
                                helperText={errors.amount}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">{currencySymbol()}</InputAdornment>,
                                    inputProps: { min: 0, step: 0.01 }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Type *</InputLabel>
                                <Select
                                    value={formData.type}
                                    onChange={(e) => handleChange('type', e.target.value)}
                                    label="Type *"
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: 400
                                            }
                                        }
                                    }}
                                >
                                    <MenuItem value="expense">Expense</MenuItem>
                                    <MenuItem value="credit">Credit</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Row 4: Category - Full Width (only for expenses) */}
                        {formData.type === 'expense' && (
                            <Grid item xs={12} md={12}>
                                <FormControl fullWidth sx={{ minWidth: 200 }}>
                                    <InputLabel>Category</InputLabel>
                                    <Select
                                        value={formData.category}
                                        onChange={(e) => handleChange('category', e.target.value)}
                                        label="Category"
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 400
                                                }
                                            }
                                        }}
                                    >
                                        <MenuItem value="">None</MenuItem>
                                        {categoryMenuItems(categories)}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        {/* Row 5: Account Type and Bank */}
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Account Type</InputLabel>
                                <Select
                                    value={formData.accountType}
                                    onChange={(e) => handleChange('accountType', e.target.value)}
                                    label="Account Type"
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: 400
                                            }
                                        }
                                    }}
                                >
                                    <MenuItem value="bank_account">Bank Account</MenuItem>
                                    <MenuItem value="credit_card">Credit Card</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Bank"
                                value={formData.bank}
                                onChange={(e) => handleChange('bank', e.target.value)}
                                placeholder="e.g., HDFC, SBI, ICICI"
                            />
                        </Grid>

                        {/* Row 6: Account ID and User */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Account ID"
                                value={formData.accountId}
                                onChange={(e) => handleChange('accountId', e.target.value)}
                                placeholder="Last 4 digits of account"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="User"
                                value={formData.user}
                                onChange={(e) => handleChange('user', e.target.value)}
                                placeholder="e.g., John Doe (for joint accounts)"
                            />
                        </Grid>

                        {/* Row 7: Tags - Full Width */}
                        <Grid item xs={12}>
                            <TagSelector
                                value={formData.tagIds}
                                onChange={(newTagIds) => handleChange('tagIds', newTagIds)}
                                allTags={tags}
                                label="Tags"
                            />
                        </Grid>
                    </Grid>
                </LocalizationProvider>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <LoadingButton
                    onClick={handleSubmit}
                    loading={loading}
                    variant="contained"
                    color="primary"
                >
                    Add Transaction
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default AddTransactionDialog;

